import React, { useRef, useState, useEffect } from 'react';
import { ChevronDown, FileText, RefreshCw } from 'lucide-react';
import {
  ActionNodeData,
  ACTION_TYPE_OPTIONS,
  AUTO_ASSIGN_STRATEGY_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  ResponseMapping,
  autoAssignSummary,
} from '../types';
import { useOrgTeams, useOrgAgents, useOrgCustomFields, useExtractionSchemas, useOrgPipelines, useOrgStages, useOrgTicketTypes } from '../hooks/useOrgData';
import { VariablePicker } from './VariablePicker';
import { apiClient } from '@/services/api-client';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Email template interface
interface EmailTemplate {
  id: string;
  name: string;
  shortcut?: string;
  content: string;
  emailSubject?: string;
  variables: string[];
}

// Email Template Selector Component
function EmailTemplateSelector({
  value,
  onChange,
  onApplyTemplate,
}: {
  value?: string;
  onChange: (templateId: string) => void;
  onApplyTemplate: (template: EmailTemplate) => void;
}) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/canned-responses/email-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to load email templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const selectedTemplate = templates.find((t) => t.id === value);

  const handleSelect = (template: EmailTemplate) => {
    onChange(template.id);
    onApplyTemplate(template);
    setShowDropdown(false);
  };

  return (
    <div className="mb-4">
      <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
        Email Template <span className="text-gray-400 dark:text-gray-500">(optional)</span>
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full flex items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-left focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <span className={selectedTemplate ? '' : 'text-gray-400 dark:text-gray-500'}>
            {selectedTemplate ? (
              <span className="flex items-center gap-2">
                <FileText className="h-3 w-3" />
                {selectedTemplate.name}
              </span>
            ) : (
              'Select a template (or write custom below)...'
            )}
          </span>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''} dark:text-gray-500`} />
        </button>
        {showDropdown && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2 dark:text-gray-400">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Loading templates...
                </div>
              ) : templates.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No email templates found. Create one in Settings → Canned Responses.
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      onChange('');
                      setShowDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400"
                  >
                    — No template (custom) —
                  </button>
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleSelect(template)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                        value === template.id
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-3 w-3 flex-shrink-0" />
                        <span>{template.name}</span>
                        {template.shortcut && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">{template.shortcut}</span>
                        )}
                      </div>
                      {(template.variables?.length ?? 0) > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5 truncate dark:text-gray-500">
                          Variables: {template.variables.slice(0, 3).join(', ')}
                          {template.variables.length > 3 && '...'}
                        </div>
                      )}
                    </button>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Guidance under the "Assign to team" picker: no teams exist yet, or the saved
 * team is no longer a valid team (deleted/deactivated — or a person's id saved
 * by the old picker, which listed users instead of teams).
 */
export function TeamPickerHint({
  loaded,
  hasTeams,
  selectedId,
  known,
}: {
  loaded: boolean;
  hasTeams: boolean;
  selectedId?: string;
  known: boolean;
}) {
  if (!loaded) return null;
  if (!hasTeams) {
    return (
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Create a team in Settings → Teams first.
      </p>
    );
  }
  if (selectedId && !known) {
    return (
      <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
        The saved team is not an active team. Please select a team.
      </p>
    );
  }
  return null;
}

/**
 * Settings for the "Auto-assign agent" action, shared by the Visual Builder and
 * the Advanced editor: the technique (required), the team to pick from
 * (optional — default is the ticket's own team, or every agent if it has none)
 * and whether tickets that already have an agent are skipped (default on).
 */
export function AutoAssignConfigFields({
  config,
  onChange,
  teams,
  teamsLoaded,
  selectClassName,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  teams: Array<{ id: string; name: string }>;
  teamsLoaded: boolean;
  selectClassName: string;
}) {
  const strategy = (config.strategy as string) || '';
  const teamId = (config.teamId as string) || '';
  const onlyIfUnassigned = config.onlyIfUnassigned !== false;

  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Assignment technique
        </legend>
        <div className="space-y-2" role="radiogroup" aria-label="Assignment technique">
          {AUTO_ASSIGN_STRATEGY_OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition-colors ${
                strategy === o.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
              }`}
            >
              <input
                type="radio"
                name="auto-assign-strategy"
                value={o.value}
                checked={strategy === o.value}
                onChange={() => onChange('strategy', o.value)}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-white">{o.label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{o.description}</span>
              </span>
            </label>
          ))}
        </div>
        {!strategy && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">Choose a technique.</p>
        )}
      </fieldset>

      <div>
        <label htmlFor="auto-assign-team" className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Pick from team <span className="text-gray-400 dark:text-gray-500">(optional)</span>
        </label>
        <ThemedSelect
          id="auto-assign-team"
          className={selectClassName}
          value={teamId}
          onChange={(e) => onChange('teamId', e.target.value)}
        >
          <option value="">Ticket's current team (all agents if it has none)</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </ThemedSelect>
        {teamId && (
          <TeamPickerHint
            loaded={teamsLoaded}
            hasTeams={teams.length > 0}
            selectedId={teamId}
            known={teams.some((t) => t.id === teamId)}
          />
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Only active agents of the team are considered. A chosen team also moves the ticket into it.
        </p>
      </div>

      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          checked={onlyIfUnassigned}
          onChange={(e) => onChange('onlyIfUnassigned', e.target.checked)}
          className="mt-0.5"
        />
        <span className="text-sm text-gray-700 dark:text-gray-200">
          Only if the ticket has no agent yet
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            Turn off to re-assign tickets that already have an agent.
          </span>
        </span>
      </label>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        If no agent is available, the ticket is left unchanged and this step is marked skipped.
      </p>
    </div>
  );
}

interface Props {
  data: ActionNodeData;
  onChange: (updated: Partial<ActionNodeData>) => void;
}

export function ActionConfig({ data, onChange }: Props) {
  const { teams, loaded: teamsLoaded } = useOrgTeams();
  const { agents } = useOrgAgents();
  const { customFields } = useOrgCustomFields('ticket');
  const { schemas: extractionSchemas } = useExtractionSchemas();
  const { pipelines } = useOrgPipelines();
  const { stages } = useOrgStages(data.config.pipelineId as string | undefined);
  const { ticketTypes } = useOrgTicketTypes();

  // Refs for textareas that support variable insertion
  const webhookBodyRef = useRef<HTMLTextAreaElement>(null);
  const emailToRef = useRef<HTMLInputElement>(null);
  const emailSubjectRef = useRef<HTMLInputElement>(null);
  const emailBodyRef = useRef<HTMLTextAreaElement>(null);
  const noteContentRef = useRef<HTMLTextAreaElement>(null);
  const createSubjectRef = useRef<HTMLInputElement>(null);
  const createDescRef = useRef<HTMLTextAreaElement>(null);

  const updateConfig = (key: string, value: unknown) => {
    const newConfig = { ...data.config, [key]: value };
    let summary = '';

    if (data.actionType === 'assign_team') {
      summary = `Team: ${teams.find((t) => t.id === value)?.name ?? value}`;
    } else if (data.actionType === 'assign_agent') {
      summary = `Agent: ${agents.find((a) => a.id === value)?.name ?? value}`;
    } else if (data.actionType === 'auto_assign') {
      summary = autoAssignSummary(newConfig, teams);
    } else if (data.actionType === 'set_priority') {
      summary = `Priority → ${value}`;
    } else if (data.actionType === 'set_status') {
      summary = `Status → ${value}`;
    } else if (data.actionType === 'send_email') {
      summary = (newConfig.to as string) ? `To: ${newConfig.to}` : (newConfig.subject as string) || 'Send email';
    } else if (data.actionType === 'send_internal_note') {
      summary = (newConfig.content as string || '').substring(0, 50) || 'Add note';
    } else if (data.actionType === 'trigger_webhook') {
      summary = newConfig.url as string || 'Call API';
    } else if (data.actionType === 'parse_email_and_update_line_item') {
      const schema = extractionSchemas.find(s => s.id === newConfig.extractionSchemaId);
      summary = schema ? `Schema: ${schema.name}` : 'Auto-detect schema';
    } else if (data.actionType === 'update_line_item') {
      const updates = (newConfig.fieldUpdates as Array<{field: string; value: string}>) || [];
      summary = updates.length > 0 ? `${updates.length} field(s)` : 'Update line item';
    } else if (data.actionType === 'fetch_line_items') {
      summary = 'Load line items into context';
    } else if (data.actionType === 'increment_retry_counter') {
      summary = `Max ${newConfig.maxRetries || 3} retries`;
    } else if (data.actionType === 'move_to_pipeline_stage') {
      const pName = pipelines.find((p) => p.id === newConfig.pipelineId)?.name;
      const sName = stages.find((s) => s.id === value)?.name ?? value;
      summary = pName ? `Move to ${pName} / ${sName}` : 'Move to stage';
    } else if (data.actionType === 'create_ticket') {
      summary = (newConfig.subject as string) ? `Create: ${newConfig.subject}` : 'Create linked ticket';
    }

    onChange({ config: newConfig, displaySummary: summary });
  };

  return (
    <div className="space-y-4">
      {/* Action type selector */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Action</label>
        <ThemedSelect
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={data.actionType}
          onChange={(e) => {
            // Auto-assign starts on Round Robin (as in the Advanced editor), so
            // a fresh block is valid and never needs a technique picked first.
            if (e.target.value === 'auto_assign') {
              const config = { strategy: 'round_robin', onlyIfUnassigned: true };
              onChange({ actionType: e.target.value, config, displaySummary: autoAssignSummary(config, teams) });
              return;
            }
            onChange({ actionType: e.target.value, config: {}, displaySummary: undefined });
          }}
        >
          <option value="">Select an action…</option>
          {ACTION_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.icon} {o.label}</option>
          ))}
        </ThemedSelect>
      </div>

      {/* Context-specific fields */}
      {data.actionType === 'assign_team' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Team</label>
          <ThemedSelect
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={(data.config.teamId as string) || ''}
            onChange={(e) => updateConfig('teamId', e.target.value)}
          >
            <option value="">{teamsLoaded && teams.length === 0 ? 'No teams yet' : 'Select team…'}</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </ThemedSelect>
          <TeamPickerHint
            loaded={teamsLoaded}
            hasTeams={teams.length > 0}
            selectedId={data.config.teamId as string | undefined}
            known={teams.some((t) => t.id === data.config.teamId)}
          />
        </div>
      )}

      {data.actionType === 'auto_assign' && (
        <AutoAssignConfigFields
          config={data.config}
          onChange={updateConfig}
          teams={teams}
          teamsLoaded={teamsLoaded}
          selectClassName="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
      )}

      {data.actionType === 'assign_agent' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Agent</label>
          <ThemedSelect
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={(data.config.agentId as string) || ''}
            onChange={(e) => updateConfig('agentId', e.target.value)}
          >
            <option value="">Select agent…</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </ThemedSelect>
        </div>
      )}

      {data.actionType === 'set_priority' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Priority</label>
          <ThemedSelect
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={(data.config.priority as string) || ''}
            onChange={(e) => updateConfig('priority', e.target.value)}
          >
            <option value="">Select…</option>
            {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </ThemedSelect>
        </div>
      )}

      {data.actionType === 'set_status' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Status</label>
          <ThemedSelect
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={(data.config.status as string) || ''}
            onChange={(e) => updateConfig('status', e.target.value)}
          >
            <option value="">Select…</option>
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </ThemedSelect>
        </div>
      )}

      {data.actionType === 'send_email' && (
        <>
          <EmailTemplateSelector
            value={(data.config.templateId as string) || ''}
            onChange={(templateId) => updateConfig('templateId', templateId)}
            onApplyTemplate={(template) => {
              // Auto-fill subject and body from template
              const newConfig = {
                ...data.config,
                templateId: template.id,
                subject: template.emailSubject || data.config.subject,
                body: template.content || data.config.body,
              };
              onChange({ config: newConfig, displaySummary: `Template: ${template.name}` });
            }}
          />
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">To</label>
              <VariablePicker
                textareaRef={emailToRef}
                onInsert={(val) => updateConfig('to', val)}
              />
            </div>
            <input
              ref={emailToRef}
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.to as string) || ''}
              onChange={(e) => updateConfig('to', e.target.value)}
              placeholder="{{entity.contactEmail}} or email@example.com"
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Subject</label>
              <VariablePicker
                textareaRef={emailSubjectRef}
                onInsert={(val) => updateConfig('subject', val)}
              />
            </div>
            <input
              ref={emailSubjectRef}
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.subject as string) || ''}
              onChange={(e) => updateConfig('subject', e.target.value)}
              placeholder="We received your ticket…"
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Message body</label>
              <VariablePicker
                textareaRef={emailBodyRef}
                onInsert={(val) => updateConfig('body', val)}
              />
            </div>
            <textarea
              ref={emailBodyRef}
              rows={4}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.body as string) || ''}
              onChange={(e) => updateConfig('body', e.target.value)}
              placeholder="Hi {{entity.contactName}}, your ticket #{{entity.ticketNumber}} has been received…"
            />
          </div>
        </>
      )}

      {data.actionType === 'send_internal_note' && (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-200">Note content</label>
            <VariablePicker
              textareaRef={noteContentRef}
              onInsert={(val) => updateConfig('content', val)}
            />
          </div>
          <textarea
            ref={noteContentRef}
            rows={3}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={(data.config.content as string) || ''}
            onChange={(e) => updateConfig('content', e.target.value)}
            placeholder="Auto-assigned via workflow…"
          />
        </div>
      )}

      {data.actionType === 'trigger_webhook' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">URL</label>
            <input
              type="url"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.url as string) || ''}
              onChange={(e) => updateConfig('url', e.target.value)}
              placeholder="https://your-api.example.com/webhook"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">HTTP Method</label>
            <ThemedSelect
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.method as string) || 'POST'}
              onChange={(e) => updateConfig('method', e.target.value)}
            >
              <option>POST</option>
              <option>GET</option>
              <option>PUT</option>
              <option>PATCH</option>
            </ThemedSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Content Type</label>
            <ThemedSelect
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.contentType as string) || 'json'}
              onChange={(e) => updateConfig('contentType', e.target.value)}
            >
              <option value="json">JSON (application/json)</option>
              <option value="form-data">Form Data (multipart/form-data)</option>
            </ThemedSelect>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-200">
                Request body {(data.config.contentType as string) === 'form-data' ? '(key-value pairs)' : '(JSON)'} <span className="text-gray-400 dark:text-gray-500">optional</span>
              </label>
              <VariablePicker
                textareaRef={webhookBodyRef}
                onInsert={(val) => updateConfig('body', val)}
                jsonMode
              />
            </div>
            <textarea
              ref={webhookBodyRef}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.body as string) || ''}
              onChange={(e) => updateConfig('body', e.target.value)}
              placeholder='{"ticketId": "{{entity.id}}", "type": "{{entity.ticketTypeName}}"}'
            />
          </div>

          {/* Response Mapping Section */}
          <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-600">
            <div className="mb-3 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Response Mapping <span className="text-gray-400 dark:text-gray-500">optional</span>
              </label>
            </div>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Map fields from the API response to custom fields on the ticket.
            </p>

            {/* Existing mappings */}
            {((data.config.responseMappings as ResponseMapping[]) || []).map((mapping, index) => (
              <div key={index} className="mb-2 flex items-center gap-2">
                <input
                  type="text"
                  className="w-1/3 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  value={mapping.responseField}
                  onChange={(e) => {
                    const mappings = [...((data.config.responseMappings as ResponseMapping[]) || [])];
                    mappings[index] = { ...mappings[index], responseField: e.target.value };
                    updateConfig('responseMappings', mappings);
                  }}
                  placeholder="response.field"
                />
                <span className="text-gray-400 dark:text-gray-500">→</span>
                <ThemedSelect
                  className="w-1/3 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  value={mapping.targetField}
                  onChange={(e) => {
                    const mappings = [...((data.config.responseMappings as ResponseMapping[]) || [])];
                    const selectedField = customFields.find(f => f.key === e.target.value);
                    mappings[index] = {
                      ...mappings[index],
                      targetField: e.target.value,
                      targetFieldName: selectedField?.name,
                    };
                    updateConfig('responseMappings', mappings);
                  }}
                >
                  <option value="">Select field…</option>
                  {customFields.map((f) => (
                    <option key={f.id} value={f.key}>{f.name}</option>
                  ))}
                </ThemedSelect>
                <button
                  type="button"
                  onClick={() => {
                    const mappings = ((data.config.responseMappings as ResponseMapping[]) || []).filter((_, i) => i !== index);
                    updateConfig('responseMappings', mappings);
                  }}
                  className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Remove mapping"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}

            {/* Add new mapping button */}
            <button
              type="button"
              onClick={() => {
                const mappings = [...((data.config.responseMappings as ResponseMapping[]) || [])];
                mappings.push({ responseField: '', targetField: '' });
                updateConfig('responseMappings', mappings);
              }}
              className="flex items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 hover:border-primary-500 hover:text-primary-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-primary-400 dark:hover:text-primary-400"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add response mapping
            </button>

            {/* Example hint for Horizon */}
            {((data.config.responseMappings as ResponseMapping[]) || []).length === 0 && (
              <div className="mt-3 rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                <strong>Example for Horizon API:</strong>
                <br />
                <code className="text-[10px]">data → Quote Link</code>
                <br />
                <code className="text-[10px]">brokerLink → Broker Portal</code>
              </div>
            )}
          </div>

          {/* Error Handling Section */}
          <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-600">
            <label className="mb-3 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Error Handling
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={(data.config.onErrorPublishQuoteFailure as boolean) || false}
                onChange={(e) => updateConfig('onErrorPublishQuoteFailure', e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
              />
              <div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  On error, trigger quote validation failure notification
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  When this API call fails with a validation error (400), automatically send an email notification to the customer with details about missing or invalid fields.
                </p>
              </div>
            </label>
          </div>
        </>
      )}

      {data.actionType === 'add_tag' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Tag</label>
          <input
            type="text"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={(data.config.tag as string) || ''}
            onChange={(e) => updateConfig('tag', e.target.value)}
            placeholder="refund-request"
          />
        </div>
      )}

      {data.actionType === 'set_custom_field' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Field ID</label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.fieldId as string) || ''}
              onChange={(e) => updateConfig('fieldId', e.target.value)}
              placeholder="account_number"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Value</label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.value as string) || ''}
              onChange={(e) => updateConfig('value', e.target.value)}
              placeholder="{{entity.metadata.account_number}}"
            />
          </div>
        </>
      )}

      {data.actionType === 'move_to_pipeline_stage' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Pipeline</label>
            <ThemedSelect
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.pipelineId as string) || ''}
              onChange={(e) => onChange({ config: { ...data.config, pipelineId: e.target.value, stageId: '' }, displaySummary: undefined })}
            >
              <option value="">Select pipeline...</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </ThemedSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Stage</label>
            <ThemedSelect
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.stageId as string) || ''}
              disabled={!data.config.pipelineId}
              onChange={(e) => updateConfig('stageId', e.target.value)}
            >
              <option value="">{data.config.pipelineId ? 'Select stage...' : 'Choose a pipeline first'}</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </ThemedSelect>
          </div>
        </>
      )}
      {data.actionType === 'create_ticket' && (
        <>
          <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            Creates a new ticket and automatically links it back to the ticket that triggered this workflow.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Ticket type</label>
            <ThemedSelect
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.ticketTypeId as string) || ''}
              onChange={(e) => updateConfig('ticketTypeId', e.target.value)}
            >
              <option value="">Select ticket type...</option>
              {ticketTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </ThemedSelect>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Subject</label>
              <VariablePicker textareaRef={createSubjectRef} onInsert={(val) => updateConfig('subject', val)} />
            </div>
            <input
              ref={createSubjectRef}
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.subject as string) || ''}
              onChange={(e) => updateConfig('subject', e.target.value)}
              placeholder="Follow-up for {{entity.ticketNumber}}"
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Description</label>
              <VariablePicker textareaRef={createDescRef} onInsert={(val) => updateConfig('description', val)} />
            </div>
            <textarea
              ref={createDescRef}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.description as string) || ''}
              onChange={(e) => updateConfig('description', e.target.value)}
              placeholder="Linked to {{entity.subject}}"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Priority</label>
              <ThemedSelect
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                value={(data.config.priority as string) || 'medium'}
                onChange={(e) => updateConfig('priority', e.target.value)}
              >
                {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </ThemedSelect>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Status</label>
              <ThemedSelect
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                value={(data.config.status as string) || 'new'}
                onChange={(e) => updateConfig('status', e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </ThemedSelect>
            </div>
          </div>
        </>
      )}
      {data.actionType === 'classify_ticket_type' && (
        <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
          The AI will automatically detect the ticket type using your organisation's defined types
          (Direct Debit, Refund, Meter Reading, etc.) and set it on the ticket.
        </p>
      )}

      {data.actionType === 'fetch_line_items' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>Fetch Line Items</strong> loads all line items for the current ticket into <code className="rounded bg-blue-100 px-1 dark:bg-blue-800">entity.lineItems</code>.
          </p>
          <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
            Use this before a Loop node to iterate over line items. The line items array will be available as <code className="rounded bg-blue-100 px-1 dark:bg-blue-800">entity.lineItems</code> for downstream nodes.
          </p>
        </div>
      )}

      {data.actionType === 'parse_email_and_update_line_item' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Extraction Schema
            </label>
            <ThemedSelect
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.extractionSchemaId as string) || ''}
              onChange={(e) => {
                const schema = extractionSchemas.find(s => s.id === e.target.value);
                const newConfig = {
                  ...data.config,
                  extractionSchemaId: e.target.value,
                  extractionSchemaName: schema?.name,
                };
                onChange({ config: newConfig, displaySummary: schema ? `Schema: ${schema.name}` : 'Parse email' });
              }}
            >
              <option value="">Auto-detect from ticket type</option>
              {extractionSchemas.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.ticketTypeName ? `(${s.ticketTypeName})` : ''} - {s.fieldsCount} fields
                </option>
              ))}
            </ThemedSelect>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select a specific schema or leave as auto-detect to use the schema matching the ticket type.
            </p>
          </div>
          <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            The AI will parse incoming customer emails/messages to extract missing line item fields
            based on the configured extraction schema. Extracted values are automatically applied to
            the associated line item.
          </p>
        </>
      )}

      {data.actionType === 'update_line_item' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Field Updates
            </label>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Specify which line item fields to update. Use variables to reference extracted or ticket data.
            </p>

            {/* Existing field mappings */}
            {((data.config.fieldUpdates as Array<{field: string; value: string}>) || []).map((update, index) => (
              <div key={index} className="mb-2 flex items-center gap-2">
                <input
                  type="text"
                  className="w-1/3 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  value={update.field}
                  onChange={(e) => {
                    const updates = [...((data.config.fieldUpdates as Array<{field: string; value: string}>) || [])];
                    updates[index] = { ...updates[index], field: e.target.value };
                    updateConfig('fieldUpdates', updates);
                  }}
                  placeholder="fieldKey"
                />
                <span className="text-gray-400 dark:text-gray-500">=</span>
                <input
                  type="text"
                  className="w-1/2 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  value={update.value}
                  onChange={(e) => {
                    const updates = [...((data.config.fieldUpdates as Array<{field: string; value: string}>) || [])];
                    updates[index] = { ...updates[index], value: e.target.value };
                    updateConfig('fieldUpdates', updates);
                  }}
                  placeholder="{{extracted.fieldKey}}"
                />
                <button
                  type="button"
                  onClick={() => {
                    const updates = ((data.config.fieldUpdates as Array<{field: string; value: string}>) || []).filter((_, i) => i !== index);
                    updateConfig('fieldUpdates', updates);
                  }}
                  className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Remove"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => {
                const updates = [...((data.config.fieldUpdates as Array<{field: string; value: string}>) || [])];
                updates.push({ field: '', value: '' });
                updateConfig('fieldUpdates', updates);
              }}
              className="flex items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 hover:border-primary-500 hover:text-primary-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-primary-400 dark:hover:text-primary-400"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add field update
            </button>
          </div>
        </>
      )}

      {data.actionType === 'increment_retry_counter' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Maximum Retries
            </label>
            <input
              type="number"
              min="1"
              max="10"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.maxRetries as number) || 3}
              onChange={(e) => {
                const maxRetries = parseInt(e.target.value, 10) || 3;
                const newConfig = { ...data.config, maxRetries };
                onChange({ config: newConfig, displaySummary: `Max ${maxRetries} retries` });
              }}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              After this many failed attempts, the workflow will stop retrying and escalate.
            </p>
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Counter Field
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.counterField as string) || 'retryCount'}
              onChange={(e) => updateConfig('counterField', e.target.value)}
              placeholder="retryCount"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The field name to store the retry count on the ticket/line item.
            </p>
          </div>
          <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            When the retry limit is reached, a <code>WORKFLOW_RETRY_LIMIT_EXCEEDED</code> event
            will be published for escalation handling.
          </p>
        </>
      )}

      {data.actionType === 'conditional' && (
        <>
          <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            <strong>If/Else Condition</strong> allows you to branch the workflow based on conditions.
            Use the Branch node in the visual builder for more complex conditional flows, or configure
            inline conditions below.
          </p>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Condition Field
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.conditionField as string) || ''}
              onChange={(e) => {
                updateConfig('conditionField', e.target.value);
                onChange({ displaySummary: `If ${e.target.value || 'condition'}` });
              }}
              placeholder="entity.status"
            />
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Operator
            </label>
            <ThemedSelect
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.operator as string) || 'equals'}
              onChange={(e) => updateConfig('operator', e.target.value)}
            >
              <option value="equals">equals</option>
              <option value="not_equals">does not equal</option>
              <option value="contains">contains</option>
              <option value="not_contains">does not contain</option>
              <option value="is_empty">is empty</option>
              <option value="is_not_empty">is not empty</option>
            </ThemedSelect>
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Value
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.conditionValue as string) || ''}
              onChange={(e) => updateConfig('conditionValue', e.target.value)}
              placeholder="in_progress"
            />
          </div>
        </>
      )}

      {data.actionType === 'switch' && (
        <>
          <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            <strong>Switch/Case</strong> routes the workflow to different paths based on the value of a field.
            Each case value triggers a different set of actions.
          </p>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Switch Field
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.switchField as string) || ''}
              onChange={(e) => {
                updateConfig('switchField', e.target.value);
                onChange({ displaySummary: `Switch on ${e.target.value || 'field'}` });
              }}
              placeholder="entity.ticketTypeId"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The field whose value determines which case to execute.
            </p>
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Case Values <span className="text-gray-400 dark:text-gray-500">(comma-separated)</span>
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.caseValues as string) || ''}
              onChange={(e) => updateConfig('caseValues', e.target.value)}
              placeholder="refund, billing, technical"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Enter the possible values. Configure actions for each case in the Advanced mode.
            </p>
          </div>
        </>
      )}

      {data.actionType === 'stop_workflow' && (
        <>
          <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            <strong>Stop Workflow</strong> immediately terminates the current workflow execution.
            No further actions will be processed after this point.
          </p>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Reason <span className="text-gray-400 dark:text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              value={(data.config.reason as string) || ''}
              onChange={(e) => {
                updateConfig('reason', e.target.value);
                onChange({ displaySummary: e.target.value ? `Stop: ${e.target.value}` : 'Stop workflow' });
              }}
              placeholder="Condition not met"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This reason will be logged in the execution history.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
